
import { AssessmentQuestion } from "@/interfaces/recruiter/candidates/create_assessment";
import Vue from "vue";

export default Vue.extend({
  name: "FloatingChatWidget",
  props: {
    assessment: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isOpen: false,
      newMessage: "",
      isTyping: false,
      hasNewMessage: false
    };
  },
  computed: {
    sessionId() {
      return this.$store.getters["recruiter/getAiSessionId"];
    },
    sessionMessage() {
      return this.$store.getters["recruiter/getAiSessionMessage"];
    },
    messages() {
      return this.$store.getters["recruiter/getChatMessages"];
    }
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      this.hasNewMessage = false;

      if (this.isOpen) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() || this.isTyping) return;

      const userMessage = this.newMessage.trim();
      this.newMessage = "";

      // Add user message
      this.$store.commit("recruiter/ADD_CHAT_MESSAGE", {
        type: "user",
        text: userMessage,
        time: this.getCurrentTime()
      });

      this.scrollToBottom();

      // Check if session exists
      if (!this.sessionId) {
        // Show error message if session is not available
        this.$store.commit("recruiter/ADD_CHAT_MESSAGE", {
          type: "ai",
          text: "AI session is not available. Please refresh the page.",
          time: this.getCurrentTime(),
          loading: false
        });
        this.isTyping = false;
        this.scrollToBottom();
        return;
      }

      // Simulate AI typing
      this.isTyping = true;
      const aiMessage = {
        type: "ai" as const,
        text: "",
        time: this.getCurrentTime(),
        loading: true
      };
      this.$store.commit("recruiter/ADD_CHAT_MESSAGE", aiMessage);
      this.scrollToBottom();

      try {
        // Send message to AI assistant
        const response = await this.$store.dispatch(
          "recruiter/CHAT_WITH_AI_ASSISTANT",
          {
            session_id: this.sessionId,
            message: userMessage
          }
        );

        if (response) {
          // Update the loading message with AI response
          const messageIndex = this.messages.length - 1;
          this.$store.commit("recruiter/UPDATE_CHAT_MESSAGE", {
            index: messageIndex,
            message: {
              type: "ai",
              text: response.message,
              time: this.getCurrentTime(),
              loading: false
            }
          });

          // Update assessment if AI provided changes
          if (response.assessment_data) {
            await this.updateAssessmentWithAI(response);
          }
        } else {
          // Fallback to local response
          const fallbackResponse = this.generateAIResponse(userMessage);
          const messageIndex = this.messages.length - 1;
          this.$store.commit("recruiter/UPDATE_CHAT_MESSAGE", {
            index: messageIndex,
            message: {
              type: "ai",
              text: fallbackResponse,
              time: this.getCurrentTime(),
              loading: false
            }
          });
        }
      } catch (error) {
        console.error("Error sending message to AI:", error);
        // Fallback to local response
        const fallbackResponse = this.generateAIResponse(userMessage);
        const messageIndex = this.messages.length - 1;
        this.$set(this.messages, messageIndex, {
          type: "ai",
          text: fallbackResponse,
          time: this.getCurrentTime(),
          loading: false
        });
      }

      this.isTyping = false;
      this.scrollToBottom();
    },

    generateAIResponse(userInput: string): string {
      const input = userInput.toLowerCase();

      if (input.includes("question") && input.includes("add")) {
        return `I can help you add a new question! Here are some suggestions:<br>
        • <strong>Multiple Choice:</strong> "What is the capital of France?" with options A) London, B) Paris, C) Berlin, D) Madrid<br>
        • <strong>True/False:</strong> "JavaScript is a programming language."<br>
        • <strong>Short Answer:</strong> "Explain the concept of recursion in programming."<br><br>
        Would you like me to help you create a specific question?`;
      }

      if (input.includes("difficulty") || input.includes("level")) {
        return `Based on your current assessment, I suggest:<br>
        • <strong>Easy:</strong> Basic concepts, simple recall questions<br>
        • <strong>Medium:</strong> Application of concepts, problem-solving<br>
        • <strong>Hard:</strong> Complex analysis, critical thinking<br><br>
        Your current difficulty is set to <strong>${this.assessment.difficulty}</strong>. Would you like to change it?`;
      }

      if (input.includes("time") || input.includes("duration")) {
        return `For optimal assessment timing, I recommend:<br>
        • <strong>Multiple Choice:</strong> 30-60 seconds per question<br>
        • <strong>True/False:</strong> 15-30 seconds per question<br>
        • <strong>Short Answer:</strong> 2-5 minutes per question<br><br>
        Would you like me to suggest timing for your current questions?`;
      }

      if (input.includes("improve") || input.includes("better")) {
        return `I can help improve your assessment by:<br>
        • Analyzing question clarity and difficulty<br>
        • Suggesting better answer options<br>
        • Checking for grammatical errors<br>
        • Ensuring proper question flow<br><br>
        Would you like me to review your current questions?`;
      }

      if (input.includes("delete") || input.includes("remove")) {
        return `I can help you identify questions to remove by:<br>
        • Finding duplicate or similar questions<br>
        • Identifying overly complex questions<br>
        • Suggesting questions that don't align with your assessment goals<br><br>
        Would you like me to analyze your questions for potential removal?`;
      }

      if (
        input.includes("hello") ||
        input.includes("hi") ||
        input.includes("hey")
      ) {
        return `Hello! 👋 I'm your AI assessment assistant. I'm here to help you create and improve your assessment questions. What would you like to work on today?`;
      }

      if (input.includes("help") || input.includes("what can you do")) {
        return `I can help you with various assessment tasks:<br><br>
        <strong>📝 Question Management:</strong><br>
        • Add new questions with AI suggestions<br>
        • Edit existing questions for clarity<br>
        • Delete unnecessary questions<br><br>
        <strong>🎯 Assessment Optimization:</strong><br>
        • Adjust difficulty levels<br>
        • Set appropriate time limits<br>
        • Improve question quality<br>
        • Analyze assessment structure<br><br>
        <strong>💡 Smart Suggestions:</strong><br>
        • Generate question ideas<br>
        • Suggest better answer options<br>
        • Check for grammatical errors<br><br>
        Just tell me what you need help with!`;
      }

      // Default response
      return `I understand you want help with "${userInput}". I can assist you with:<br>
      • Adding new questions<br>
      • Editing existing questions<br>
      • Improving question quality<br>
      • Adjusting difficulty levels<br>
      • Setting appropriate time limits<br>
      • Analyzing assessment structure<br><br>
      Please be more specific about what you'd like to work on!`;
    },

    getCurrentTime(): string {
      return new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const chatMessages = this.$refs.chatMessages as HTMLElement;
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      });
    },

    async updateAssessmentWithAI(aiResponse: {
      session_id: string;
      status: string;
      message: string;
      current_step: string;
      assessment_data: {
        id: number;
        assessment_name: string;
        assessment_type: string;
        difficulty: string;
        is_active: boolean;
        questions: AssessmentQuestion[];
      };
      deleted_question_ids: number[];
    }) {
      try {
        const questions = Array.isArray(aiResponse.assessment_data.questions)
          ? aiResponse.assessment_data.questions.map((question) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { order: _, ...rest } = question;
              return {
                ...rest
              };
            })
          : [];
        const isNewQuestionAdded =
          questions.filter((question) => !question.id).length > 0;
        const success = await this.$store.dispatch(
          "recruiter/UPDATE_ASSESSMENT_WITH_AI",
          {
            assessment_id: aiResponse.assessment_data.id,
            assessment_name: aiResponse.assessment_data.assessment_name,
            assessment_type: aiResponse.assessment_data.assessment_type,
            difficulty: aiResponse.assessment_data.difficulty,
            is_active: aiResponse.assessment_data.is_active ?? true,
            questions,
            deleted_question_ids: Array.isArray(aiResponse.deleted_question_ids)
              ? aiResponse.deleted_question_ids
              : []
          }
        );

        if (success) {
          // Emit event to parent component to refresh questions
          this.$emit("assessment-updated", isNewQuestionAdded);
        }
      } catch (error) {
        console.error("Error updating assessment with AI:", error);
        this.$store.commit("ROOT_ERROR", {
          message: "Failed to update assessment with AI changes",
          type: "error"
        });
      }
    }
  },
  watch: {
    sessionId: {
      handler(newVal) {
        if (newVal && this.sessionId) {
          // Add welcome message when session is created
          const welcomeMessage =
            this.sessionMessage ||
            "AI session created successfully! How can I help you with your assessment?";

          this.$store.commit("recruiter/ADD_CHAT_MESSAGE", {
            type: "ai",
            text: welcomeMessage,
            time: this.getCurrentTime()
          });

          this.scrollToBottom();
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Set initial time for the first message if it exists
    if (this.messages.length > 0 && !this.messages[0].time) {
      this.$store.commit("recruiter/UPDATE_CHAT_MESSAGE", {
        index: 0,
        message: {
          ...this.messages[0],
          time: this.getCurrentTime()
        }
      });
    }
  }
});
